import React, { useEffect, useState } from "react";
import { FooterComponents } from "../FooterComponent";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { Plus, Trash2, Save, Edit, X, CheckCircle, AlertCircle, BarChart3, Target } from "lucide-react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getToken } from "../../utils/auth";

export default function AbroadSelectionCriteria() {
  const { programId } = useParams();
  const navigate = useNavigate();
  
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [totalWeight, setTotalWeight] = useState(0);
  
  const [newCriteria, setNewCriteria] = useState({
    criteriaName: "",
    weightAllocated: "",
    evaluationGuideline: "",
    evaluationScaleTemplate: ""
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    criteriaName: "",
    weightAllocated: "",
    evaluationGuideline: "",
    evaluationScaleTemplate: ""
  });

  useEffect(() => {
    fetchCriteria();
  }, [programId]);

  useEffect(() => {
    // Calculate total weight whenever criteria changes
    const total = criteria.reduce((sum, criterion) => sum + (criterion.weightAllocated || 0), 0);
    setTotalWeight(total);
  }, [criteria]);

  const fetchCriteria = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-criteria/get-by-education-program?educationProgramId=${programId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`
          },
        }
      );
      setCriteria(response.data || []);
    } catch (err) {
      setError("Failed to fetch selection criteria: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, isEdit = false) => {
    const { name, value } = e.target;
    if (isEdit) {
      setEditFormData(prev => ({
        ...prev,
        [name]: name === "weightAllocated" ? parseInt(value) || "" : value
      }));
    } else {
      setNewCriteria(prev => ({
        ...prev,
        [name]: name === "weightAllocated" ? parseInt(value) || "" : value
      }));
    }
  };

  const validateForm = (data) => {
    if (!data.criteriaName.trim()) {
      toast.error("Criteria name is required");
      return false;
    }
    if (!data.weightAllocated || data.weightAllocated <= 0 || data.weightAllocated > 100) {
      toast.error("Weight must be between 1 and 100");
      return false;
    }
    if (!data.evaluationGuideline.trim()) {
      toast.error("Evaluation guideline is required");
      return false;
    }
    if (!data.evaluationScaleTemplate.trim()) {
      toast.error("Evaluation scale template is required");
      return false;
    }
    
    // Check if total weight will exceed 100
    const currentTotal = criteria.reduce((sum, criterion) => {
      // If editing, exclude the current criterion from calculation
      if (editingId && criterion.id === editingId) {
        return sum;
      }
      return sum + (criterion.weightAllocated || 0);
    }, 0);
    
    if (currentTotal + data.weightAllocated > 100) {
      toast.error(`Total weight cannot exceed 100%. Current total: ${currentTotal}%`);
      return false;
    }
    
    return true;
  };

  const handleAddCriteria = async () => {
    if (!validateForm(newCriteria)) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-criteria/save?educationProgramId=${programId}`,
        {
          criteriaName: newCriteria.criteriaName,
          weightAllocated: parseInt(newCriteria.weightAllocated),
          evaluationGuideline: newCriteria.evaluationGuideline,
          evaluationScaleTemplate: newCriteria.evaluationScaleTemplate
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Selection criteria added successfully!");
        setNewCriteria({
          criteriaName: "",
          weightAllocated: "",
          evaluationGuideline: "",
          evaluationScaleTemplate: ""
        });
        setIsAddingNew(false);
        fetchCriteria();
      }
    } catch (err) {
      console.error("Error adding criteria:", err);
      toast.error(err.response?.data?.message || "Failed to add selection criteria");
    }
  };

  const handleEditCriteria = (criterion) => {
    setEditingId(criterion.id);
    setEditFormData({
      criteriaName: criterion.criteriaName,
      weightAllocated: criterion.weightAllocated,
      evaluationGuideline: criterion.evaluationGuideline,
      evaluationScaleTemplate: criterion.evaluationScaleTemplate
    });
  };

  const handleUpdateCriteria = async () => {
    if (!validateForm(editFormData)) return;

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-criteria/update`,
        {
          id: editingId,
          criteriaName: editFormData.criteriaName,
          weightAllocated: parseInt(editFormData.weightAllocated),
          evaluationGuideline: editFormData.evaluationGuideline,
          evaluationScaleTemplate: editFormData.evaluationScaleTemplate,
          educationProgram: { id: programId }
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Selection criteria updated successfully!");
        setEditingId(null);
        setEditFormData({
          criteriaName: "",
          weightAllocated: "",
          evaluationGuideline: "",
          evaluationScaleTemplate: ""
        });
        fetchCriteria();
      }
    } catch (err) {
      console.error("Error updating criteria:", err);
      toast.error(err.response?.data?.message || "Failed to update selection criteria");
    }
  };

  const handleDeleteCriteria = async (criteriaId) => {
    if (window.confirm("Are you sure you want to delete this criteria?")) {
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-criteria/delete?id=${criteriaId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        if (response.status === 200) {
          toast.success("Selection criteria deleted successfully!");
          fetchCriteria();
        }
      } catch (err) {
        console.error("Error deleting criteria:", err);
        toast.error(err.response?.data?.message || "Failed to delete selection criteria");
      }
    }
  };

  const cancelAdd = () => {
    setIsAddingNew(false);
    setNewCriteria({
      criteriaName: "",
      weightAllocated: "",
      evaluationGuideline: "",
      evaluationScaleTemplate: ""
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFormData({
      criteriaName: "",
      weightAllocated: "",
      evaluationGuideline: "",
      evaluationScaleTemplate: ""
    });
  };

  const getWeightColor = () => {
    if (totalWeight === 100) return "text-green-600";
    if (totalWeight > 100) return "text-red-600";
    return "text-yellow-600";
  };

  const getWeightBgColor = () => {
    if (totalWeight === 100) return "bg-green-50 border-green-200";
    if (totalWeight > 100) return "bg-red-50 border-red-200";
    return "bg-yellow-50 border-yellow-200";
  };

  const manageEvaluationScales = (criteriaId) => {
    navigate(`/abroad-selection-evaluation-scale/${criteriaId}`);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="max-w-7xl mx-auto py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Selection Criteria Management
                  </h1>
                  <p className="text-gray-600">
                    Define and manage evaluation criteria for this education program.
                  </p>
                </div>
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ← Back to Programs
                </button>
              </div>
            </div>

            {/* Weight Summary */}
            <div className={`rounded-lg border p-6 mb-6 ${getWeightBgColor()}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BarChart3 className={`w-6 h-6 ${getWeightColor()}`} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Weight Allocation</h3>
                    <p className="text-sm text-gray-600">Total weight must equal 100%</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getWeightColor()}`}>
                    {totalWeight}%
                  </div>
                  <div className="text-sm text-gray-600">
                    {totalWeight === 100 ? (
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Complete
                      </span>
                    ) : totalWeight > 100 ? (
                      <span className="flex items-center text-red-600">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Exceeds 100%
                      </span>
                    ) : (
                      <span className="text-yellow-600">
                        {100 - totalWeight}% remaining
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Add New Button */}
            <div className="mb-6">
              <button
                onClick={() => setIsAddingNew(true)}
                disabled={isAddingNew || totalWeight >= 100}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Selection Criteria
              </button>
              {totalWeight >= 100 && (
                <p className="mt-2 text-sm text-amber-600">
                  Cannot add new criteria. Weight allocation is at {totalWeight}%.
                </p>
              )}
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                  <BarChart3 className="w-8 h-8 text-indigo-600" />
                </div>
                <p className="text-gray-600">Loading criteria...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                <p className="font-medium">Error: {error}</p>
                <p>Please try again later.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Criteria Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Weight (%)
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Evaluation Guideline
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Scale Template
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Add New Form Row */}
                      {isAddingNew && (
                        <tr className="bg-blue-50">
                          <td className="px-6 py-4">
                            <input
                              type="text"
                              name="criteriaName"
                              value={newCriteria.criteriaName}
                              onChange={(e) => handleInputChange(e, false)}
                              placeholder="Enter criteria name"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="number"
                              name="weightAllocated"
                              value={newCriteria.weightAllocated}
                              onChange={(e) => handleInputChange(e, false)}
                              placeholder="0-100"
                              min="1"
                              max="100"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <textarea
                              name="evaluationGuideline"
                              value={newCriteria.evaluationGuideline}
                              onChange={(e) => handleInputChange(e, false)}
                              placeholder="Enter evaluation guidelines"
                              rows="2"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="text"
                              name="evaluationScaleTemplate"
                              value={newCriteria.evaluationScaleTemplate}
                              onChange={(e) => handleInputChange(e, false)}
                              placeholder="e.g., Excellent/Good/Poor"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={handleAddCriteria}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Save
                              </button>
                              <button
                                onClick={cancelAdd}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                              >
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Existing Criteria Rows */}
                      {criteria.map((criterion) => (
                        <tr key={criterion.id} className={editingId === criterion.id ? "bg-yellow-50" : "hover:bg-gray-50"}>
                          <td className="px-6 py-4">
                            {editingId === criterion.id ? (
                              <input
                                type="text"
                                name="criteriaName"
                                value={editFormData.criteriaName}
                                onChange={(e) => handleInputChange(e, true)}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            ) : (
                              <div className="text-sm font-medium text-gray-900">
                                {criterion.criteriaName}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {editingId === criterion.id ? (
                              <input
                                type="number"
                                name="weightAllocated"
                                value={editFormData.weightAllocated}
                                onChange={(e) => handleInputChange(e, true)}
                                min="1"
                                max="100"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {criterion.weightAllocated}%
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {editingId === criterion.id ? (
                              <textarea
                                name="evaluationGuideline"
                                value={editFormData.evaluationGuideline}
                                onChange={(e) => handleInputChange(e, true)}
                                rows="2"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            ) : (
                              <div className="text-sm text-gray-900 max-w-xs truncate">
                                {criterion.evaluationGuideline}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {editingId === criterion.id ? (
                              <input
                                type="text"
                                name="evaluationScaleTemplate"
                                value={editFormData.evaluationScaleTemplate}
                                onChange={(e) => handleInputChange(e, true)}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            ) : (
                              <div className="text-sm text-gray-900">
                                {criterion.evaluationScaleTemplate}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            {editingId === criterion.id ? (
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={handleUpdateCriteria}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                  <Save className="w-4 h-4 mr-2" />
                                  Update
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={() => manageEvaluationScales(criterion.id)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                  title="Manage Evaluation Scales"
                                >
                                  <Target className="w-4 h-4 mr-2" />
                                  Scales
                                </button>
                                <button
                                  onClick={() => handleEditCriteria(criterion)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </button>
                                {/* <button
                                  onClick={() => handleDeleteCriteria(criterion.id)}
                                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </button> */}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}

                      {criteria.length === 0 && !isAddingNew && (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center">
                            <div className="text-gray-500">
                              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No selection criteria found
                              </h3>
                              <p>Get started by adding your first selection criteria.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        <div>
          <FooterComponents />
        </div>
      </div>
    </div>
  );
}